import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, LogOut, RefreshCw, Search, Mail, Phone, Calendar as CalIcon,
  Users, Home as HomeIcon, ShieldCheck, Video, ClipboardList, MessageSquare, Loader2,
  Download, Image as ImageIcon, Settings,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { adminMe, adminStats, adminInquiries, clearToken, getToken } from '../lib/adminApi';
import SEO from '../components/SEO';

const BASE = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  contact:             { label: 'Contact',         icon: MessageSquare, color: 'bg-primary' },
  seminar:             { label: 'Seminar',         icon: Users,         color: 'bg-brand' },
  video:               { label: 'Video',           icon: Video,         color: 'bg-primary' },
  visa_quick:          { label: 'Visa (Quick)',    icon: ShieldCheck,   color: 'bg-brand' },
  visa_full:           { label: 'Visa (Full)',     icon: ShieldCheck,   color: 'bg-primary' },
  accommodation:       { label: 'Accommodation',   icon: HomeIcon,      color: 'bg-brand' },
  accommodation_full:  { label: 'Accom. Message',  icon: HomeIcon,      color: 'bg-primary' },
};

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-6 shadow-sm">
      <div className="text-xs uppercase tracking-widest text-brand font-bold">{label}</div>
      <div className="mt-2 font-serif text-4xl font-extrabold text-primary">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function TypePill({ type, active, count, onClick }) {
  const m = TYPE_META[type] || { label: type, icon: MessageSquare };
  const Icon = m.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`filter-${type || 'all'}`}
      className={`inline-flex items-center gap-2 h-9 px-4 rounded-full border text-xs font-semibold transition-all
        ${active
          ? 'bg-brand text-white border-brand shadow-md shadow-brand/30'
          : 'bg-white text-primary border-border hover:border-brand/50 hover:text-brand'}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {type ? m.label : 'All'}
      {typeof count === 'number' && (
        <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-muted'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [type, setType] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (t = type) => {
    setRefreshing(true);
    try {
      const [s, list] = await Promise.all([
        adminStats(),
        adminInquiries({ type: t, limit: 500 }),
      ]);
      setStats(s);
      setRows(list);
    } catch (err) {
      if (err.status === 401) {
        clearToken();
        nav('/admin/login', { replace: true });
      }
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [nav, type]);

  useEffect(() => {
    if (!getToken()) { nav('/admin/login', { replace: true }); return; }
    (async () => {
      try {
        const me = await adminMe();
        setAdmin(me);
      } catch {
        nav('/admin/login', { replace: true });
      }
    })();
  }, [nav]);

  useEffect(() => {
    if (admin) load(type);
  }, [admin, type, load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(s) ||
      (r.phone || '').toLowerCase().includes(s) ||
      (r.email || '').toLowerCase().includes(s) ||
      (r.city || '').toLowerCase().includes(s) ||
      (r.message || '').toLowerCase().includes(s)
    );
  }, [rows, q]);

  const logout = () => {
    clearToken();
    nav('/admin/login', { replace: true });
  };

  const exportCsv = async () => {
    const token = getToken();
    if (!token) { nav('/admin/login', { replace: true }); return; }
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    params.set('limit', '10000');
    try {
      const res = await fetch(`${BASE}/api/admin/inquiries.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { clearToken(); nav('/admin/login', { replace: true }); return; }
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disp = res.headers.get('content-disposition') || '';
      const match = disp.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `inquiries_${type || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      /* silent — user can retry */
    }
  };

  if (loading && !admin) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center text-primary">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const types = Object.keys(TYPE_META);
  const byType = stats?.by_type || {};

  return (
    <div className="bg-muted/40 min-h-[calc(100vh-80px)]" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard — Russian Education" description="Manage inquiries and leads." noindex />

      {/* Top strip */}
      <div className="bg-primary text-white">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-6 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand text-white flex items-center justify-center shadow-lg shadow-brand/40">
              <LayoutDashboard className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/80 font-semibold">Admin</div>
              <h1 className="font-serif text-xl md:text-2xl font-bold">Inquiries Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-[11px] uppercase tracking-widest text-white/70">Signed in as</div>
              <div className="text-sm font-semibold" data-testid="admin-email">{admin?.email}</div>
            </div>
            <Button
              data-testid="admin-cms-link"
              onClick={() => nav('/admin/cms')}
              className="bg-brand hover:bg-brand/90 text-white h-10 px-4 border-2 border-white/20 shadow-lg shadow-brand/40"
            >
              <Settings className="w-4 h-4 mr-1.5" /> Content CMS
            </Button>
            <Button
              data-testid="admin-gallery-link"
              onClick={() => nav('/admin/gallery')}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-10 px-4"
            >
              <ImageIcon className="w-4 h-4 mr-1.5" /> Gallery
            </Button>
            <Button
              data-testid="admin-uni-logos-link"
              onClick={() => nav('/admin/universities')}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-10 px-4"
            >
              <ImageIcon className="w-4 h-4 mr-1.5" /> University Logos
            </Button>
            <Button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              className="bg-white text-primary hover:bg-white/90 h-10 px-4"
            >
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button
              data-testid="admin-refresh"
              onClick={() => load(type)}
              disabled={refreshing}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-10 px-4"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              data-testid="admin-logout"
              onClick={logout}
              className="bg-brand hover:bg-brand-dark text-white h-10 px-4"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 md:px-8 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Inquiries" value={stats?.total ?? '—'} sub="All time" />
          <StatCard label="Last 24 hours" value={stats?.last_24h ?? '—'} sub="New in a day" />
          <StatCard label="Contact Forms" value={byType.contact || 0} sub="About page" />
          <StatCard label="Seminars" value={byType.seminar || 0} sub="Seat reservations" />
        </div>

        {/* Filters + Search */}
        <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
          <div className="flex flex-wrap items-center gap-2" data-testid="type-filters">
            <TypePill type={null} active={type === null} count={stats?.total} onClick={() => setType(null)} />
            {types.map((t) => (
              <TypePill key={t} type={t} active={type === t} count={byType[t] || 0} onClick={() => setType(t)} />
            ))}
          </div>

          <div className="mt-4 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, phone, email, city or message…"
              className="w-full h-11 pl-9 pr-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden" data-testid="inquiries-table">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div className="font-serif font-bold text-primary">
              {filtered.length} {filtered.length === 1 ? 'inquiry' : 'inquiries'}
              {type ? <span className="text-brand"> · {TYPE_META[type]?.label}</span> : null}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              {refreshing ? 'Loading…' : 'No inquiries match the current filters.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-primary text-[11px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-5 py-3 font-bold">Type</th>
                    <th className="text-left px-5 py-3 font-bold">Name</th>
                    <th className="text-left px-5 py-3 font-bold">Phone</th>
                    <th className="text-left px-5 py-3 font-bold">Email</th>
                    <th className="text-left px-5 py-3 font-bold">Details</th>
                    <th className="text-left px-5 py-3 font-bold">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => {
                    const m = TYPE_META[r.type] || { label: r.type, icon: MessageSquare, color: 'bg-primary' };
                    const Icon = m.icon;
                    return (
                      <tr key={r.id} className="hover:bg-muted/40 transition-colors" data-testid={`row-${r.id}`}>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-[11px] font-bold ${m.color}`}>
                            <Icon className="w-3.5 h-3.5" /> {m.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 font-semibold text-primary">{r.name}</td>
                        <td className="px-5 py-3">
                          <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 text-primary hover:text-brand">
                            <Phone className="w-3.5 h-3.5" /> {r.phone}
                          </a>
                        </td>
                        <td className="px-5 py-3">
                          {r.email ? (
                            <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 text-primary hover:text-brand">
                              <Mail className="w-3.5 h-3.5" /> {r.email}
                            </a>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-5 py-3 text-muted-foreground max-w-[280px]">
                          <div className="space-y-0.5">
                            {r.city && <div><b>City:</b> {r.city}</div>}
                            {r.state && <div><b>State:</b> {r.state}</div>}
                            {r.slot && <div><b>Slot:</b> {r.slot}{r.date ? ` · ${r.date}` : ''}</div>}
                            {r.topic && <div><b>Topic:</b> {r.topic}</div>}
                            {r.neet && <div><b>NEET:</b> {r.neet}</div>}
                            {r.variant && <div><b>Variant:</b> {r.variant}</div>}
                            {r.source && <div><b>Page:</b> <code className="text-[10px]">{r.source}</code></div>}
                            {r.message && <div className="line-clamp-2">{r.message}</div>}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            <CalIcon className="w-3.5 h-3.5" /> {fmtDate(r.created_at)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
